import { ApprovalsService } from 'api-clients/monolith';

import {
  ALWAYS,
  ConditionDraft,
  ConditionLogic,
  draftConditions,
  emptyCondition,
  FieldCatalog,
  isScopingCondition,
  mintConditionKey,
  passthroughConditions,
  scopePassthroughConditions,
  StoredCondition,
  toStoredConditions,
  validateCondition,
} from './conditionForm';

import {
  ApprovalRule,
  ApprovalRuleApprover,
  ApprovalRuleLevel,
  SubjectType,
} from './types';

/**
 * The write shape, taken from the generated client rather than described again
 * here — so a contract change surfaces as a type error in `toPayload` instead of
 * as a 422 at runtime.
 */
export type RulePayload = Parameters<
  typeof ApprovalsService.postInternalApiApprovalsRules
>[0];

/** One level's approver rows, as the write endpoint expects them. */
type ApproverRows = NonNullable<
  NonNullable<RulePayload['rule']['levels']>[number]['approvers']
>;

/**
 * The rule as the panel edits it.
 *
 * Deliberately not the API shape. `conditions`, `condition_logic` and the
 * escalation fields are carried on each level untouched — see `toPayload` for
 * why that matters — while the panel only ever writes `name` and `approvers`.
 */
export interface LevelDraft {
  /** Stable within one editing session so React keys survive a reorder. */
  key: string;
  name: string;
  /**
   * Carried, never edited (AXHE-4470): the locked design decision is that only
   * whole rules have an enable/disable toggle, so the panel's per-level switch
   * was removed. Kept on the draft because `toPayload` has to name it — see the
   * note there — and because the panel says so out loud on a level that is off.
   */
  enabled: boolean;
  approverIds: string[];
  /**
   * User approvers the API returned without a `user_id`, i.e. whose user has
   * since been offboarded. They cannot be round-tripped and are dropped on save
   * — see `draftFor`. Counted so the panel can say so out loud rather than
   * quietly shrinking the level.
   *
   * Counts **primary and backup** rows. A dropped backup is the same loss of
   * coverage as a dropped primary, and the panel cannot show the backup roster
   * at all, so leaving them out would make the warning silent for the one case
   * the admin has no other way to see.
   */
  offboardedApproverCount: number;
  /** The data-key conditions this level is gated on (AXHE-3929). */
  conditions: ConditionDraft[];
  /**
   * How they combine — the And/Or connector shown between rows (AXHE-3930).
   *
   * Lifted out of `passthrough` now that the builder renders it. Round-tripping
   * it there was correct while nothing showed it, but a field that is both
   * spread from passthrough and written by the panel would have the stale copy
   * win on save.
   */
  conditionLogic: ConditionLogic;
  /** Condition types this builder does not render, carried through untouched. */
  conditionPassthrough: StoredCondition[];
  /**
   * The level's conditions exactly as the API returned them.
   *
   * Kept so a save with no catalog re-emits what was there rather than a set
   * rebuilt by a builder that could not read it. Without the catalog the builder
   * is not rendered and these rows cannot have been edited, so replaying them is
   * the definition of leaving them alone — not a coincidence of
   * `toStoredConditions` happening to round-trip.
   */
  storedConditions: StoredCondition[];
  /** Round-tripped, never edited here. */
  passthrough: Omit<
    ApprovalRuleLevel,
    | 'name'
    | 'enabled'
    | 'approvers'
    | 'position'
    | 'conditions'
    | 'condition_logic'
  >;
}

/**
 * What a rule's scope says, as the panel's radio pair puts it (AXHE-4467).
 *
 * The two states the stored scope actually has. `all` covers both ways "applies
 * to everything" is written — no conditions at all, and a lone `always` row.
 */
export type ScopeChoice = 'all' | 'conditions';

/** Keys minted for scope condition rows, kept clear of any level's. */
export const SCOPE_CONDITION_PREFIX = 'scope';

export interface RuleDraft {
  name: string;
  subjectType: SubjectType;
  /**
   * Carried, never edited here (AXHE-4470). A rule's one enable/disable toggle
   * lives in its list row, which PATCHes `enabled` on its own; the panel's
   * duplicate switch was removed. Round-tripped so saving an edit does not
   * disarm — or arm — a gate the admin came here to rename.
   */
  enabled: boolean;
  levels: LevelDraft[];
  /**
   * Whether the rule gates everything or only what its conditions select.
   *
   * Held as its own field rather than derived from `scopeConditions.length`,
   * because the conditional branch has to be selectable *before* it has a
   * condition in it — the mock opens that branch with an empty row waiting to be
   * filled, and a derived flag would snap straight back to `all`.
   */
  scopeChoice: ScopeChoice;
  /** The rule's data-key scope conditions, edited here (AXHE-4467). */
  scopeConditions: ConditionDraft[];
  /** Scope condition types this builder does not render, carried untouched. */
  scopeConditionPassthrough: StoredCondition[];
  /**
   * The scope exactly as the API returned it.
   *
   * Two jobs. It is what a save with no field catalog re-emits, for the reason
   * `LevelDraft#storedConditions` gives. And it is where the `always` rows are
   * read back from on the way out, so choosing "All offers" keeps whichever of
   * the two encodings the rule already had rather than rewriting it.
   */
  storedScopeConditions: StoredCondition[];
  scopeConditionLogic: ConditionLogic;
}

export const emptyLevel = (key: string): LevelDraft => ({
  key,
  name: '',
  // The only value a level added here can have. Nothing can switch it off, and
  // `SaveRule#create_level` defaults an absent `enabled` to true anyway — naming
  // it keeps the payload honest about a field the panel still sends.
  enabled: true,
  approverIds: [],
  offboardedApproverCount: 0,
  conditions: [],
  // Matches the column default. A level whose conditions all have to hold is the
  // stricter reading, and the one an admin who has not touched the connector
  // gets either way.
  conditionLogic: 'and',
  conditionPassthrough: [],
  storedConditions: [],
  passthrough: {},
});

export const draftForNew = (subjectType: SubjectType): RuleDraft => ({
  name: '',
  subjectType,
  // A new rule starts active: an admin who just wrote a gate meant it to gate.
  // Sent explicitly rather than left out — `SaveRule#rule_attributes` defaults a
  // *create* to `enabled: false`, "because a governance gate that switches
  // itself on is the wrong direction to fail", so omission would file every new
  // rule inert with nothing on screen saying so.
  enabled: true,
  levels: [emptyLevel('level-0')],
  // The mock's default, and the wider net of the two: a rule that gates
  // everything is the one an admin who skips this choice ends up with, and it
  // fails toward asking for approval rather than away from it.
  scopeChoice: 'all',
  scopeConditions: [],
  scopeConditionPassthrough: [],
  storedScopeConditions: [],
  scopeConditionLogic: 'and',
});

/**
 * The scope rows this panel is not showing the admin.
 *
 * Anything that narrows the rule and is not on screen: the condition types the
 * builder does not render (position, location, opening, attribute — AXHE-4383),
 * and, when the catalog never arrived, the data-key rows too, since the builder
 * renders its own error state instead of them.
 *
 * The panel uses this to withhold the "All …" choice. Selecting it means "drop
 * every condition on this rule", and a panel that cannot show a condition must
 * not offer to delete it — the admin would be agreeing to lose scoping they have
 * no way to read.
 *
 * ## A data-key row naming a departed field is NOT hidden
 *
 * Deliberately, and it was counted here for a while. `draftConditions` selects
 * on `type` alone, so such a row is drafted and rendered like any other — with
 * an empty field select and `conditionFieldUnavailable` under it ("This field is
 * no longer available. Choose another."), which is the message AXHE-3929 added
 * for exactly this. Counting it as hidden made the note above it false while the
 * row sat directly beneath, and disabled the "All …" escape that
 * `scopeNeedsCondition` points at — so removing the stale row left the rule
 * unsaveable either way, with its error naming a control that was greyed out.
 *
 * It also protects nothing. `scopeError` refuses the save as `bad_condition`
 * until the row is resolved, so a departed field cannot round-trip through a
 * select with no matching option: the admin repoints it, removes it, or does not
 * save. Hiding it instead would put a broken gate beyond reach of the one panel
 * that can fix it, and the engine already reads an unresolvable scope field as
 * no-match — the rule has silently stopped gating, which is the thing an admin
 * most needs to see.
 *
 * @param catalog the loaded field catalog, or `undefined` when it has not
 *   arrived
 */
export const hiddenScopeConditions = (
  draft: RuleDraft,
  catalog?: FieldCatalog,
): StoredCondition[] =>
  catalog
    ? draft.scopeConditionPassthrough
    : draft.storedScopeConditions.filter(isScopingCondition);

/**
 * Whether saving this rule as conditional would silently drop a stored `always`
 * row that sits beside a scoping one.
 *
 * `scopeChoice` is read row by row (`scopeDraftFor`): a scope holding both an
 * `always` row and a scoping row resolves to `conditions`, and `scopeRows` then
 * emits only the scoping rows — `scopePassthroughConditions` holds `always`
 * back. That drop is correct: choosing "conditions" means the rule narrows, and
 * an `always` row makes it not. But under `or` logic the *stored* rule matches
 * everything today — `ConditionEvaluator#all?` short-circuits `any?` on the
 * `always` row — so the drop changes what the rule gates, from all subjects to
 * the ones the scoping rows select. Silent, it is the failure this panel exists
 * to prevent: the display already reads "meet conditions" while the gate is
 * open, and the save quietly makes the display true.
 *
 * Gated on the catalog, because that is what decides whether the drop actually
 * happens. `scopeRows` only rebuilds the scope through `toStoredConditions` —
 * which excludes `always` — when the catalog is present; without it the stored
 * scope is replayed verbatim and the `always` row survives. Saying "saving
 * removes it" without the catalog would be a claim the save does not make.
 *
 * Not reachable from anything the editor writes — `always` beside a scoping row
 * comes from the API or legacy data — so this narrows to a note, not a block.
 * The note names the drop; whether it also *narrows* depends on the logic, which
 * the panel reads from `scopeConditionLogic`.
 */
export const scopeDropsAlways = (
  draft: RuleDraft,
  catalog?: FieldCatalog,
): boolean =>
  catalog !== undefined &&
  draft.scopeChoice === 'conditions' &&
  draft.storedScopeConditions.some(row => row.type === ALWAYS) &&
  draft.storedScopeConditions.some(isScopingCondition);

const isRoleApprover = (approver: ApprovalRuleApprover) =>
  approver.approver_type === 'role';

/**
 * A role approver narrowed from the read shape to the write shape.
 *
 * The read type widens `approver_type` to string and makes the role fields
 * nullable; carrying those straight back would not satisfy the write contract.
 */
const roleRow = (approver: ApprovalRuleApprover) => ({
  approver_type: 'role' as const,
  role_name: approver.role_name ?? undefined,
  role_scope: approver.role_scope ?? undefined,
});

/**
 * A *user* approver with no user_id is one whose user has been offboarded:
 * `RuleLevelApprover#user` is scoped to live users, so the serializer emits
 * `user_id: null` for them. They are genuinely unsendable — the write contract
 * requires a user_id and `SaveRule#user_for` resolves it through
 * `account.users`, which excludes deleted users. That matches the engine, which
 * already resolves them to nobody (`ApproverResolver#resolve_user` returns []
 * for a nil user). The defect was only that it happened invisibly, so they are
 * counted and the panel says so.
 */
const offboardedCount = (approvers: ApprovalRuleApprover[] = []) =>
  approvers.filter(approver => !isRoleApprover(approver) && !approver.user_id).length;

/**
 * An approver list reduced to the rows the write endpoint will accept.
 *
 * Dropping the offboarded rows is not cosmetic. Sent back as
 * `{ approver_type: 'user', user_id: null }` the server does not skip them:
 * `SaveRule#user_for` returns nil for a blank user_id, and
 * `RuleLevelApprover#identity_matches_approver_type` then fails the row with
 * "User must be set for a user approver" — which fails the *whole* save with a
 * 422. Filtering here is what keeps a rule editable after one of its approvers
 * has left.
 */
const sendableApprovers = (approvers: ApprovalRuleApprover[] = []): ApproverRows =>
  approvers
    .filter(approver => isRoleApprover(approver) || approver.user_id)
    .map(approver =>
      isRoleApprover(approver)
        ? roleRow(approver)
        : { approver_type: 'user' as const, user_id: approver.user_id as string },
    );

/**
 * The scope half of the draft, read out of the stored condition rows.
 *
 * Its own function so `draftFor` stays a single expression: the split needs the
 * stored rows named, and every field below is derived from that one array.
 */
const scopeDraftFor = (rule: ApprovalRule) => {
  const stored = (rule.scope_conditions ?? []) as StoredCondition[];

  return {
    // Read from what the scope *does*, not from a stored flag: there is no
    // column saying which radio was picked, and there does not need to be. A
    // scope that narrows nothing — no rows, or nothing but `always` — is the
    // "All …" case, and every other scope selects.
    scopeChoice: (stored.some(isScopingCondition)
      ? 'conditions'
      : 'all') as ScopeChoice,
    scopeConditions: draftConditions(stored),
    scopeConditionPassthrough: scopePassthroughConditions(stored),
    storedScopeConditions: stored,
    scopeConditionLogic: rule.scope_condition_logic ?? 'and',
  };
};

export const draftFor = (rule: ApprovalRule): RuleDraft => ({
  name: rule.name ?? '',
  subjectType: rule.subject_type ?? 'OfferLetter',
  enabled: rule.enabled ?? true,
  ...scopeDraftFor(rule),
  levels: (rule.levels ?? []).map((level, index) => {
    // `backup_approvers` is pulled out of the passthrough rest on purpose. The
    // panel does not edit backups — they only apply once the escalation timer
    // expires — so they were previously round-tripped verbatim, which sent an
    // offboarded backup straight back as `user_id: null` and made the rule
    // unsavable from a panel that cannot even show the row. They are
    // round-tripped still, but through the same filter and narrowing the
    // primary rows go through.
    //
    // `conditions` is pulled out for the same reason in reverse: this builder
    // *does* edit them, so they must not be spread back verbatim from
    // `passthrough` on top of the edited set.
    //
    // `condition_logic` joins them for the third reason: the connector between
    // condition rows is now editable, so it has an owner in the draft and must
    // not also arrive by way of the passthrough spread.
    const {
      name,
      enabled,
      approvers,
      backup_approvers: backups,
      position,
      conditions,
      condition_logic: conditionLogic,
      ...passthrough
    } = level;
    const stored = (conditions ?? []) as StoredCondition[];

    return {
      // Keyed by array index rather than `position`. Both survive a reorder
      // equally — a reorder rewrites the array and the keys are assigned once
      // per draft — but `position ?? index` can collide if one level carries a
      // position and its neighbour does not (level 0 with a null position and
      // level 1 with position 0 both become `level-0`), and two levels sharing a
      // key would share a validation message and a form field id.
      key: `level-${index}`,
      conditions: draftConditions(stored),
      conditionLogic: conditionLogic ?? 'and',
      conditionPassthrough: passthroughConditions(stored),
      storedConditions: stored,
      name: name ?? '',
      enabled: enabled ?? true,
      // Role approvers have no user_id. They are round-tripped through
      // `passthrough` rather than being dropped, so editing a rule that names a
      // role does not quietly delete that role from the level.
      approverIds: (approvers ?? [])
        .filter(approver => !isRoleApprover(approver) && approver.user_id)
        .map(approver => approver.user_id as string),
      offboardedApproverCount: offboardedCount(approvers) + offboardedCount(backups),
      passthrough: {
        ...passthrough,
        // Only re-added when the level actually carried the key, so a level the
        // API did not describe backups for still submits without it.
        ...(backups && { backup_approvers: sendableApprovers(backups) }),
        // Narrowed to the *write* shape here rather than at submit, so the draft
        // holds only things it can actually send.
        roleApprovers: (approvers ?? []).filter(isRoleApprover).map(roleRow),
      } as LevelDraft['passthrough'],
    };
  }),
});

/**
 * The rule's scope switched to the other choice.
 *
 * Opening the conditional branch seeds a row when there is nothing to show,
 * which is how the mock opens it — a branch selected and visibly empty reads as
 * "this rule has no conditions" rather than as an invitation to write one. Only
 * when there is nothing at all: a rule already scoped by a type this builder does
 * not render has conditions, and an empty data-key row would be a field the admin
 * never asked for and now has to fill.
 *
 * The drafted rows are kept when switching back to "All …" rather than cleared.
 * `toPayload` does not emit them in that state, so nothing reaches the server —
 * and an admin who picks the wrong radio does not lose what they had typed.
 */
export const withScopeChoice = (
  draft: RuleDraft,
  scopeChoice: ScopeChoice,
): RuleDraft => {
  const needsSeedRow =
    scopeChoice === 'conditions' &&
    draft.scopeConditions.length === 0 &&
    draft.scopeConditionPassthrough.length === 0;

  return {
    ...draft,
    scopeChoice,
    scopeConditions: needsSeedRow
      ? [
          emptyCondition(
            mintConditionKey(SCOPE_CONDITION_PREFIX, draft.scopeConditions),
          ),
        ]
      : draft.scopeConditions,
  };
};

/**
 * The levels with the one at `from` moved to `to`.
 *
 * Array order *is* the level order: `toPayload` deliberately omits `position`
 * and lets the server number the levels from the sequence it is sent, so moving
 * an entry here is the whole edit — there is no position field left behind to
 * fall out of step with what the panel is showing.
 *
 * A copy rather than a splice in place, because the draft is React state.
 */
export const reorderLevels = (
  levels: LevelDraft[],
  from: number,
  to: number,
): LevelDraft[] => {
  const reordered = [...levels];
  const [moved] = reordered.splice(from, 1);
  reordered.splice(to, 0, moved);
  return reordered;
};

export type LevelError = 'no_approvers' | 'bad_condition';

/**
 * What is wrong with the rule's scope, when anything is.
 *
 * `no_conditions` is a rule set to apply only to what meets conditions, with
 * none — which the engine reads as applying to *everything* (`all?` is true for
 * an empty set), the opposite of what the panel is showing. Left to save it
 * would gate every offer under a rule that says it gates some.
 *
 * `bad_condition` needs no message of its own: the rows carry their own, the
 * same way a level's do.
 *
 * The two cannot both hold — a scope with no rows has no bad ones — so this is
 * one value rather than the list a level's errors need.
 */
export type ScopeError = 'no_conditions' | 'bad_condition';

export interface ValidationErrors {
  name?: 'required';
  scope?: ScopeError;
  /**
   * Keyed by level key, so the message lands on the level that is wrong, and
   * carrying **every** problem on that level rather than the first.
   *
   * A level with no approvers and a half-written condition has two things wrong
   * with it. Reporting one, then the other after the admin fixes it and saves
   * again, is a round trip per mistake — the same argument `ValidatePlacements`
   * already states for a form that submits its whole set in one request.
   */
  levels: Record<string, LevelError[]>;
}

/**
 * The rule's scope as the API wants it (AXHE-4467).
 *
 * ## "All …" keeps the encoding the rule already had
 *
 * Applying to everything is stored two ways — no rows at all, and a lone
 * `always` row — and `ConditionEvaluator#all?` answers both the same. So this
 * re-emits whichever the rule arrived with rather than normalising: a rule
 * written as `always` by the design's own trigger vocabulary keeps saying
 * `always` after an edit that only renamed it. What it does drop is every row
 * that *narrows* the rule, which is what choosing "All …" means. The panel
 * withholds that choice when it cannot show those rows — see
 * `hiddenScopeConditions`.
 *
 * ## The conditional branch drops `always`
 *
 * A scope of `[always, data_key…]` under `or` logic matches everything, so a
 * rule reading "Offers that meet conditions" would gate every offer. Under `and`
 * the row is merely redundant. Either way it contradicts the choice the admin
 * just made, so it goes.
 */
const scopeRows = (
  draft: RuleDraft,
  catalog?: FieldCatalog,
): StoredCondition[] => {
  if (draft.scopeChoice === 'all') {
    return draft.storedScopeConditions.filter(row => !isScopingCondition(row));
  }

  // Without the catalog the scope builder rendered its own error state instead
  // of these rows, so they cannot have been edited and are replayed as they came
  // — the same reason a level's are.
  return catalog
    ? toStoredConditions(
        draft.scopeConditions,
        draft.scopeConditionPassthrough,
        catalog,
      )
    : draft.storedScopeConditions;
};

/**
 * @param catalog as for `validate` — without it a scope condition cannot be
 *   judged, but whether the rule would send any at all still can be
 */
const scopeError = (
  draft: RuleDraft,
  catalog?: FieldCatalog,
): ScopeError | undefined => {
  if (draft.scopeChoice !== 'conditions') return undefined;

  // Judged on the rows this draft would actually *send*, not on the drafts the
  // panel is holding. The two come apart when the catalog never arrived:
  // selecting this branch seeds a draft row, but `scopeRows` re-emits the stored
  // scope rather than the drafts — so a new rule saved that way sent `[]`, which
  // `ConditionEvaluator#all?` reads as *everything*. A rule that says it gates
  // some offers and gates all of them is the wrong direction to fail, and it was
  // reachable because the panel withheld only the other radio.
  //
  // Asking the payload also gets the case the old count-based check needed a
  // second clause for: a rule scoped only by a condition type this builder does
  // not render still sends that row, so it is scoped and saves — refusing it
  // would strand every rule authored before AXHE-4383 lands the rest of the
  // catalog.
  if (!scopeRows(draft, catalog).some(isScopingCondition)) return 'no_conditions';

  if (
    catalog &&
    draft.scopeConditions.some(condition => validateCondition(condition, catalog))
  ) {
    return 'bad_condition';
  }

  return undefined;
};

/**
 * @param catalog the loaded field catalog, or `undefined` when it has not
 *   arrived. Without it the condition builder is not rendered, so a condition
 *   cannot have been edited and there is nothing here to judge it against —
 *   validating anyway would flag every stored condition as unknown and block a
 *   save that only touched approvers.
 */
export const validate = (
  draft: RuleDraft,
  catalog?: FieldCatalog,
): ValidationErrors => {
  const levels: ValidationErrors['levels'] = {};

  draft.levels.forEach(level => {
    const roleApprovers =
      (level.passthrough as { roleApprovers?: ApproverRows }).roleApprovers ?? [];
    const problems: LevelError[] = [];

    // A level is staffed by named users OR by roles resolved at submission. Only
    // counting users would report a role-only level as empty and refuse to save
    // a rule the server considers perfectly valid.
    if (level.approverIds.length === 0 && roleApprovers.length === 0) {
      problems.push('no_approvers');
    }

    // A half-written condition would be saved as one the engine cannot
    // evaluate, which holds every offer for a human rather than gating the ones
    // the admin meant.
    if (
      catalog &&
      level.conditions.some(condition => validateCondition(condition, catalog))
    ) {
      problems.push('bad_condition');
    }

    if (problems.length > 0) levels[level.key] = problems;
  });

  return {
    name: draft.name.trim() ? undefined : 'required',
    scope: scopeError(draft, catalog),
    levels,
  };
};

/** Whether a level was flagged for this specific problem. */
export const levelHasError = (
  errors: ValidationErrors,
  levelKey: string,
  error: LevelError,
) => (errors.levels[levelKey] ?? []).includes(error);

export const hasErrors = (errors: ValidationErrors) =>
  errors.name !== undefined ||
  errors.scope !== undefined ||
  Object.keys(errors.levels).length > 0;

/**
 * The draft as the API wants it.
 *
 * ## Why every level carries its conditions back
 *
 * PATCH replaces the level tree wholesale — "Sent, it replaces the whole tree —
 * a level absent from the array is deleted" — and the controller reads each
 * level's conditions straight out of the submitted document
 * (`condition_rows(raw_level[:conditions])`), where a missing key becomes `[]`.
 *
 * So a panel that submitted levels without their conditions would not leave them
 * alone: it would silently **clear** them, turning a rule that gates offers over
 * $100k into one that gates every offer. Round-tripping them is what makes it
 * safe for this panel to edit names and approvers without owning the condition
 * builder (AXHE-4107).
 *
 * The same argument holds for the scope condition types this panel does not
 * render — see `scopeRows`.
 *
 * @param catalog the loaded field catalog, or `undefined`. Without it the
 *   builder never rendered, so each level's conditions are re-emitted exactly as
 *   the API returned them rather than rebuilt from drafts read without the
 *   vocabulary needed to interpret them.
 */
export const toPayload = (draft: RuleDraft, catalog?: FieldCatalog): RulePayload => ({
  rule: {
    name: draft.name.trim(),
    subject_type: draft.subjectType,
    enabled: draft.enabled,
    scope_conditions: scopeRows(draft, catalog),
    scope_condition_logic: draft.scopeConditionLogic,
    levels: draft.levels.map(level => {
      const { roleApprovers = [], ...otherFields } =
        level.passthrough as { roleApprovers?: ApproverRows };

      return {
        ...otherFields,
        // Edited rows plus the condition types this builder does not render, so
        // editing a data-key condition cannot delete a rule's position or
        // location scoping along with it.
        conditions: catalog
          ? toStoredConditions(level.conditions, level.conditionPassthrough, catalog)
          : level.storedConditions,
        // Sent unconditionally, unlike `conditions`. It is read straight off the
        // level rather than rebuilt from the catalog, so the draft holds the
        // stored value even when the builder never rendered — and `SaveRule`
        // defaults a missing key to 'and', which would otherwise flip an OR
        // level back to AND on a save that only touched approvers.
        condition_logic: level.conditionLogic,
        // `undefined`, not `null`: a level's name is nullable when read back but
        // the write contract only accepts a string or its absence, and an
        // unnamed level is one the payload simply does not name.
        name: level.name.trim() || undefined,
        // Re-emitted, not decided (AXHE-4470). The panel no longer offers a
        // level toggle, but PATCH replaces the level tree wholesale and
        // `SaveRule#create_level` defaults an absent `enabled` to **true** — so
        // dropping this key would not be inert, it would silently re-activate a
        // level an admin had switched off and put approvals back through an
        // approver they meant to bypass. Carrying the value forward is what
        // makes removing the switch a UI change rather than a data migration.
        enabled: level.enabled,
        // Position is deliberately absent: the server assigns it from this
        // array's order, so the client cannot desync the numbering from the
        // sequence it is displaying.
        approvers: [
          ...level.approverIds.map(userId => ({
            approver_type: 'user' as const,
            user_id: userId,
          })),
          ...roleApprovers,
        ],
      };
    }),
  },
});
